import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Bug, Lightbulb, HelpCircle, ThumbsUp, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { key: "bug", label: "Bug", icon: Bug },
  { key: "idea", label: "Idea", icon: Lightbulb },
  { key: "question", label: "Question", icon: HelpCircle },
  { key: "praise", label: "Praise", icon: ThumbsUp },
];

/** Floating Feedback button + dialog. Drop anywhere in the tree. */
export default function FeedbackButton({ variant = "floating" }) {
  const [open, setOpen] = useState(false);
  let cls;
  if (variant === "floating")
    cls = "fixed right-4 bottom-24 lg:bottom-4 z-40 inline-flex items-center gap-2 bg-[#ff4500] text-white rounded-full px-4 py-2.5 font-display font-black text-sm shadow-glow hover:bg-[#cc3500]";
  else if (variant === "inline")
    cls = "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold rounded-full bg-[#ff4500] text-white hover:bg-[#cc3500] shrink-0";
  else
    cls = "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold rounded-full bg-white/10 text-white hover:bg-white/20";
  return (
    <>
      <button data-testid="feedback-button" onClick={() => setOpen(true)} className={cls}>
        <MessageSquare className="w-4 h-4" /> Feedback
      </button>
      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function FeedbackDialog({ open, onClose }) {
  const [cat, setCat] = useState("idea");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!msg.trim()) return toast.error("Please describe your feedback");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/feedback`, {
        category: cat, message: msg, email,
        page: typeof window !== "undefined" ? window.location.pathname : "",
      });
      toast.success(`Thanks! Feedback ${r.data.feedback.display_id} sent to support@trupathadvisory.com`);
      setMsg(""); setEmail(""); setCat("idea");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#ff4500]" /> Send feedback</DialogTitle>
          <DialogDescription className="text-[12px] text-[#1a1a1a]/65">Goes directly to TruPath Advisory · support@trupathadvisory.com.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Category</label>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORIES.map(({ key, label, icon: Icon }) => {
                const on = cat === key;
                return (
                  <button key={key} data-testid={`feedback-cat-${key}`} type="button" onClick={() => setCat(key)}
                    className={`px-2 py-2 rounded-xl text-[10px] uppercase tracking-wider font-bold inline-flex flex-col items-center gap-1 border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15"}`}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Your email (optional · helps us follow up)</label>
            <input data-testid="feedback-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">What's on your mind?</label>
            <textarea data-testid="feedback-message" value={msg} onChange={(e) => setMsg(e.target.value)}
              placeholder="A bug to report, an idea to make hubCMD better, or a question…"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-28" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="feedback-submit" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send feedback"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
